/**
 * Inline-media embed helpers shared between BookmarkDetail (owner)
 * and SharePage / PublicCategoryPage (public).
 *
 * The goal: whenever a saved URL points at a platform that allows
 * iframe embedding, render the player inline rather than bouncing the
 * visitor off to the source. Currently supported:
 *
 *   • YouTube (watch URLs, youtu.be short links, /shorts/*)
 *   • Spotify (track, album, playlist, episode, show)
 *   • Apple Music (song via `?i=`, album, playlist; book embed widget)
 *   • SoundCloud (track and playlist via official player widget)
 *
 * Non-supported platforms (TikTok, Instagram, Facebook video, raw
 * mp4 hosting) fall through to the thumbnail-with-play-button →
 * open-original-tab behaviour each page already implements.
 */

// ── YouTube ─────────────────────────────────────────────────────────
/** Returns the YouTube video id, or null if `url` isn't a YouTube link. */
export function getYouTubeId(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (u.hostname.includes('youtu.be')) {
            return u.pathname.slice(1).split('/')[0] || null;
        }
        if (u.hostname.includes('youtube.com')) {
            const v = u.searchParams.get('v');
            if (v) return v;
            const m = u.pathname.match(/^\/(?:shorts|embed)\/([A-Za-z0-9_-]{6,})/);
            if (m) return m[1];
        }
    } catch (_e) { /* malformed URL */ }
    return null;
}

// ── Spotify ─────────────────────────────────────────────────────────
/**
 * Returns a Spotify embed URL, or null if `url` isn't a Spotify link
 * that maps to a track/album/playlist/episode/show. If the bookmark
 * backend already populated `embedUrl`, we trust it.
 */
export function getSpotifyEmbedUrl(url, embedUrl) {
    if (embedUrl) return embedUrl;
    if (!url) return null;
    try {
        const m = url.match(/open\.spotify\.com\/(track|album|playlist|episode|show)\/([a-zA-Z0-9]+)/);
        if (m) return `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator&theme=0`;
    } catch (_e) { /* swallow */ }
    return null;
}

// ── Apple Music ─────────────────────────────────────────────────────
/**
 * Returns `{ src, height }` for an Apple Music embed, or null if the
 * URL isn't an Apple Music page. The embed widget at `embed.music.apple.com`
 * accepts the same path as the public site — we just swap the hostname.
 *
 * Heights:
 *   • song / album-with-`?i=` track anchor → 175 (compact single-row)
 *   • album / playlist / station          → 450 (full tracklist)
 */
export function getAppleMusicEmbed(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (!u.hostname.endsWith('music.apple.com')) return null;
        // Pattern: /<country>/<kind>/<slug>/<id>
        // Kinds: album, song, playlist, music-video, station
        const segs = u.pathname.split('/').filter(Boolean);
        if (segs.length < 3) return null;
        const kind = segs[1];
        const supported = ['album', 'song', 'playlist', 'music-video', 'station'];
        if (!supported.includes(kind)) return null;
        // Swap host. Preserve the `?i=<song id>` param (deep-links into
        // a single track of an album) and any other query params.
        const src = `https://embed.music.apple.com${u.pathname}${u.search}`;
        // Songs / music-videos / album-anchored tracks render compact;
        // full albums + playlists + stations need the taller widget.
        const isCompact = kind === 'song' || kind === 'music-video' || (kind === 'album' && u.searchParams.has('i'));
        return { src, height: isCompact ? 175 : 450 };
    } catch (_e) { /* swallow */ }
    return null;
}

// ── TikTok ──────────────────────────────────────────────────────────
/**
 * Returns `{ src, height }` for TikTok's official iframe embed, or null.
 * Accepts both canonical `/@user/video/{id}` URLs and a pre-extracted
 * `tiktokVideoId` (which the backend stores for us on ingest via oEmbed
 * — needed because `vt.tiktok.com/{code}` shortlinks don't contain the
 * video id client-side).
 */
export function getTikTokEmbed(url, tiktokVideoId) {
    let id = tiktokVideoId || null;
    if (!id && url) {
        try {
            const m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
            if (m) id = m[1];
        } catch (_e) { /* swallow */ }
    }
    if (!id) return null;
    // Use TikTok's `/player/v1/` endpoint (the external-site player)
    // instead of `/embed/v2/` (the in-app widget). Difference:
    //   • /embed/v2/  behaves like the TikTok app - autoplays, swipe-to-
    //                 next-video, tap-to-skip. Terrible in a bookmark
    //                 viewer because a single tap sends the user to a
    //                 *different* video on tiktok.com.
    //   • /player/v1/ is a plain video player - standard tap-to-pause,
    //                 standard scrubber, no next-video overlay. Same
    //                 API TikTok recommends for third-party embeds.
    // Query params disable everything but essential controls; captions
    // on by default (users read videos silently more than they think).
    const params = new URLSearchParams({
        music_info: '0',
        description: '0',
        rel: '0',
        native_context_menu: '0',
        closed_caption: '1',
        autoplay: '0',
        loop: '0',
        controls: '1',
    }).toString();
    // 16:9-ish container is wrong for TikTok's 9:16 vertical - 605px is
    // TikTok's recommended card width, 738px gets the aspect right.
    return { src: `https://www.tiktok.com/player/v1/${id}?${params}`, height: 738 };
}

// ── SoundCloud ──────────────────────────────────────────────────────
/**
 * Returns `{ src, height }` for a SoundCloud widget embed, or null
 * if `url` isn't a SoundCloud track/set page. SoundCloud's widget
 * accepts any public track / playlist URL via the `url=` param.
 *
 * Heights:
 *   • single track  → 166 (their official compact size)
 *   • /sets/<slug>  → 450 (their official tracklist size)
 */
export function getSoundCloudEmbed(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (!/(^|\.)soundcloud\.com$/.test(u.hostname)) return null;
        const segs = u.pathname.split('/').filter(Boolean);
        // Need at least <user>/<track-or-sets>
        if (segs.length < 2) return null;
        const isPlaylist = segs[1] === 'sets';
        const src = `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false`;
        return { src, height: isPlaylist ? 450 : 166 };
    } catch (_e) { /* swallow */ }
    return null;
}

// ── Unified entry point ─────────────────────────────────────────────
/**
 * Returns `{ kind, src, height }` for the FIRST inline-embeddable
 * platform that matches the URL, or null. Used by InlineMediaPlayer
 * so adding a new platform is a single new clause here.
 *   kind: 'youtube' | 'spotify' | 'apple-music' | 'soundcloud'
 *   src:   iframe src
 *   height: numeric height in px (null → caller uses 16:9 video aspect)
 */
export function getInlineEmbed(url, spotifyEmbedUrl, tiktokVideoId) {
    const ytId = getYouTubeId(url);
    if (ytId) return { kind: 'youtube', src: `https://www.youtube.com/embed/${ytId}?rel=0`, height: null };

    const sp = getSpotifyEmbedUrl(url, spotifyEmbedUrl);
    if (sp) return { kind: 'spotify', src: sp, height: sp.includes('/embed/track/') ? 152 : 352 };

    const am = getAppleMusicEmbed(url);
    if (am) return { kind: 'apple-music', src: am.src, height: am.height };

    const tk = getTikTokEmbed(url, tiktokVideoId);
    if (tk) return { kind: 'tiktok', src: tk.src, height: tk.height };

    const sc = getSoundCloudEmbed(url);
    if (sc) return { kind: 'soundcloud', src: sc.src, height: sc.height };

    return null;
}

/** True if the URL points at a platform whose media plays inline. */
export function isEmbeddableMediaUrl(url, spotifyEmbedUrl, tiktokVideoId) {
    return !!getInlineEmbed(url, spotifyEmbedUrl, tiktokVideoId);
}
